import {
  Box,
  Checkbox,
  Flex,
  Form,
  FormGroup,
  HR,
  Input,
  Select,
  Small,
  Text,
  Textarea,
} from '@bigcommerce/big-design';
import type { ReceiptTemplateOverride } from '@kynetik/shared';

import type { LocationFormData, ReceiptMethod } from './types';
import { RECEIPT_METHOD_OPTIONS } from './types';

interface ReceiptsSettingsTabProps {
  formData: LocationFormData;
  onChange: (data: Partial<LocationFormData>) => void;
}

export function ReceiptsSettingsTab({
  formData,
  onChange,
}: ReceiptsSettingsTabProps) {
  const receiptOverride = formData.receiptOverride || {};

  const updateReceiptOverride = (field: keyof ReceiptTemplateOverride, value: string | boolean | undefined) => {
    const updated = { ...receiptOverride, [field]: value || undefined };
    // Remove undefined values to keep the object clean
    const cleaned = Object.fromEntries(
      Object.entries(updated).filter(([, v]) => v !== undefined && v !== '')
    ) as ReceiptTemplateOverride;
    onChange({
      receiptOverride: Object.keys(cleaned).length > 0 ? cleaned : undefined,
    });
  };

  return (
    <Form>
      <FormGroup>
        <Select
          label="Default Receipt Method"
          description="How receipts are sent by default at checkout"
          options={[...RECEIPT_METHOD_OPTIONS]}
          value={formData.defaultReceiptMethod}
          onOptionChange={(value) =>
            value && onChange({ defaultReceiptMethod: value as ReceiptMethod })
          }
        />
      </FormGroup>

      <Box marginTop="medium">
        <Text bold marginBottom="xSmall">Customer Information Requirements</Text>
        <Small color="secondary60" marginBottom="small">
          Require staff to collect customer contact info at checkout
        </Small>
        <Flex flexDirection="column" flexGap="small">
          <Checkbox
            label="Require customer email"
            description="Email must be captured before completing checkout"
            checked={formData.requireCustomerEmail}
            onChange={() => onChange({ requireCustomerEmail: !formData.requireCustomerEmail })}
          />
          <Checkbox
            label="Require customer phone"
            description="Phone number must be captured before completing checkout"
            checked={formData.requireCustomerPhone}
            onChange={() => onChange({ requireCustomerPhone: !formData.requireCustomerPhone })}
          />
        </Flex>
      </Box>

      <HR marginVertical="medium" />

      <Box>
        <Text bold marginBottom="xSmall">Receipt Template Overrides</Text>
        <Small color="secondary60" marginBottom="medium">
          Override the default receipt template for this store location.
          Leave fields empty to use the merchant default from the Receipts settings page.
        </Small>

        <FormGroup>
          <Input
            label="Logo URL"
            description="Override the logo for receipts at this location"
            value={receiptOverride.logoUrl || ''}
            onChange={(e) => updateReceiptOverride('logoUrl', e.target.value)}
            placeholder="Leave empty to use default"
          />
        </FormGroup>

        <FormGroup>
          <Input
            label="Business Name"
            description="Override the business name shown on receipts"
            value={receiptOverride.businessName || ''}
            onChange={(e) => updateReceiptOverride('businessName', e.target.value)}
            placeholder="Leave empty to use default"
          />
        </FormGroup>

        <FormGroup>
          <Textarea
            label="Header Text"
            description="Override the text displayed at top of receipts"
            value={receiptOverride.headerText || ''}
            onChange={(e) => updateReceiptOverride('headerText', e.target.value)}
            placeholder="Leave empty to use default"
            rows={2}
          />
        </FormGroup>

        <FormGroup>
          <Textarea
            label="Footer Text"
            description="Override the text displayed at bottom of receipts"
            value={receiptOverride.footerText || ''}
            onChange={(e) => updateReceiptOverride('footerText', e.target.value)}
            placeholder="Leave empty to use default"
            rows={2}
          />
        </FormGroup>
      </Box>
    </Form>
  );
}
